/**
 * Material UI theme configuration for Exelizer.
 * 
 * Follows Gordian Technologies Brand Kit:
 * - Dark-first interface
 * - DM Sans for UI, DM Mono for code
 * - Black/Charcoal/Graphite primary colors
 * - Forest Green, Muted Blue, Deep Purple, Burnt Red accents (used sparingly)
 * - Calm, quiet, capable, precise design
 */

import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      // Graphite as primary
      main: '#242836',
      light: '#3a3f52',
      dark: '#1a1d28',
    },
    secondary: {
      // Muted Blue as secondary (used sparingly)
      main: '#6b7a9e',
      light: '#8a9bb8',
      dark: '#4d5a75',
    },
    error: {
      // Burnt Red for errors
      main: '#8b4513',
      light: '#a0522d',
      dark: '#6b3410',
    },
    warning: {
      // Burnt Red variant for warnings
      main: '#a0522d',
      light: '#c17a4a',
      dark: '#7a3d1f',
    },
    info: {
      // Muted Blue for info
      main: '#6b7a9e',
      light: '#8a9bb8',
      dark: '#4d5a75',
    },
    success: {
      // Forest Green for success
      main: '#2d5016',
      light: '#3d6b1f',
      dark: '#1f350f',
    },
    background: {
      // Black as default background
      default: '#050609',
      paper: '#0F1116', // Charcoal for cards/surfaces
    },
    text: {
      primary: '#F3F4F6', // Primary text color
      secondary: '#9AA0B6', // Muted text color
    },
  },
  typography: {
    // DM Sans for UI (fallback to system fonts if not loaded)
    fontFamily: [
      '"DM Sans"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      color: '#F3F4F6',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#F3F4F6',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#F3F4F6',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#F3F4F6',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#F3F4F6',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#F3F4F6',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
          // Subtle shadows only
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          // Matte texture, subtle depth, no gloss
          backgroundColor: '#0F1116', // Charcoal
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          // No glassmorphism
          backdropFilter: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#0F1116', // Charcoal
          // Matte texture
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          // Rounded corners for icons
          borderRadius: 4,
        },
      },
    },
  },
});

